import { collection, getDocs, query, where } from 'firebase/firestore';
import type { QueryDocumentSnapshot } from 'firebase/firestore';

import { getFirebaseFirestore } from '@/lib/firebase/firestore';
import type { CandidateDoc } from '@/types/backend-contracts';
import { buildCandidatesQueryKey } from '@/lib/query-cache/query-keys';
import { resolveBrowserQueryCache, type BrowserQueryCache } from '@/lib/query-cache/query-cache';

const candidateCacheTags = ['question-answer'];

async function getCandidateDocs(cache: BrowserQueryCache = resolveBrowserQueryCache()): Promise<Array<CandidateDoc & { id: string }>> {
  const result = await cache.read(
    buildCandidatesQueryKey(),
    async () => {
      const snapshot = await getDocs(query(collection(getFirebaseFirestore(), 'candidates'), where('status', '==', 'active')));

      return snapshot.docs.map((document: QueryDocumentSnapshot) => ({
        id: document.id,
        ...(document.data() as CandidateDoc),
      }));
    },
    {
      staleTimeMs: 5 * 60 * 1000,
      cacheTimeMs: 30 * 60 * 1000,
      tags: ['catalog:candidates'],
      invalidateTagsOnFreshFetch: candidateCacheTags,
    },
  );

  return result.value;
}

export async function getActiveCandidates(options?: { cache?: BrowserQueryCache }): Promise<Array<CandidateDoc & { id: string }>> {
  return getCandidateDocs(options?.cache);
}

export async function getCandidateBySlug(
  slug: string,
  options?: { cache?: BrowserQueryCache },
): Promise<(CandidateDoc & { id: string }) | null> {
  const candidates = await getCandidateDocs(options?.cache);
  return candidates.find((candidate) => candidate.slug === slug || candidate.id === slug) ?? null;
}

export async function getCandidateById(
  candidateId: string,
  options?: { cache?: BrowserQueryCache },
): Promise<(CandidateDoc & { id: string }) | null> {
  return getCandidateBySlug(candidateId, options);
}
